import type { AdjustmentWhyValue } from '@shared-ui/components';
import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { OperationNavStrip, type OperationNav } from './OperationNav';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { adminApi } from '../../api/adminApi';
import { correctnessFromScore } from '@padcanvas/shared';

/** The console acts for the student who owns the attempt — the correction, the
 *  re-read and the submission it produces are all theirs. */
const correctionApi = (studentId: string) =>
    createRegionCorrectionApi({ getAuthHeaders: () => getAuthHeaders() as Record<string, string> }, studentId);

const POLL_INTERVAL_MS = 3_000;
const POLL_MAX_MS = 5 * 60_000;
import type { AnswerSecondRead, ModelOutput, Submission } from '../../types';
import { classifySupersededRuns, type SupersededReason } from './supersededRuns';
import { triggerLabel } from '../../types';
import { sentStudentImage, sentStudentImagesOf, studentImageLabel } from '../../utils/attachedImages';
// PromptInfo type used via usePromptLookup from shared-ui
import { ClientInfoItems } from '@shared-ui/components';
import { ItemTagActions } from '@shared-ui/user-tags';
import {
    OperationDetailPage,
    ContextGrid,
    UserProfileCard,
    DetailItem,
    MetadataCard,
    MetaItem,
    QuestionCard,
    StudentWorkCard,
    GivenSolutionCard,
    RunsTable,
    RunDetailCard,
    ErrorBanner,
    type RunsTableColumn,
    usePromptLookup,
    buildRunColumnMap,
} from '@shared-ui/operation-detail';
import { ParsedFeedbackBody, getRawOutputText } from '@shared-ui/parsed-feedback';
import { AnalysisJudgeVerdicts } from './AnalysisJudgeVerdicts';
import { CanvasReplayModal } from '../../components/CanvasReplay';
import { RerunDialog } from './RerunDialog';
import { ReEvaluateDialog } from './ReEvaluateDialog';
import { RelatedOperationsDialog } from './RelatedOperationsDialog';
import { RegionCorrector, createRegionCorrectionApi } from '@shared-ui/solution-upload';
import { getAuthHeaders } from '../../api/fetchHelper';
import { AddToBenchButton } from './AddToBenchButton';
import type { DetectLearningMomentsResult } from '../../api/modelApi';
import '../scenario-evals/BenchTab.css';
import { AdminFeedbackPanel } from './AdminFeedbackPanel';
import { EvidencePanel } from './EvidencePanel';
import { ScoreOverridePanel } from './ScoreOverridePanel';
import { RunSummary } from './RunSummary';
import { RunDetailHeading } from './RunDetailHeading';
import { QualityCheckBadges } from './parsedFeedback';
import './SolutionAnalysisDetail.css';

function formatTime(seconds?: number): string {
    if (seconds === undefined || seconds === null) return '-';
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
}

/** Map a 0-10 final score to a CSS class for color-coding the score chip.
 *  Replaces the prior `overallAssessment`-driven mapping; the assessment
 *  field is no longer carried over the wire to frontends. */
const getScoreClass = (score?: number) => {
    const band = correctnessFromScore(score);   // canonical >=7 / >=4
    return band === 'correct' ? 'correct'
        : band === 'partially_correct' ? 'partial'
        : band === 'incorrect' ? 'incorrect'
        : '';
};


export function SolutionAnalysisDetail() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    /** The page of rows this detail was opened from, if it was opened from one.
     *  Absent on a direct link, which is why the strip is conditional. */
    const opNav = (location.state as { nav?: OperationNav } | null)?.nav;
    // Callers outside the Operations list (e.g. learning-moment encounters) pass
    // an explicit backTo/backLabel so "back" returns to where the row was clicked.
    const navState = location.state as { backSearch?: string; backTo?: string; backLabel?: string } | null;
    const backSearch = navState?.backSearch;
    const queryClient = useQueryClient();
    const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

    // Rerun state
    const [rerunStatus, setRerunStatus] = useState<'idle' | 'running' | 'success' | 'error'>('idle');
    const [rerunResult, setRerunResult] = useState<{ newSubmissionId: string } | null>(null);
    const [rerunError, setRerunError] = useState<string | null>(null);
    const [showRerunDialog, setShowRerunDialog] = useState(false);

    // Replay modal state
    const [showReplayModal, setShowReplayModal] = useState(false);
    // Open when someone here decides this question was read off the wrong region.
    const [correcting, setCorrecting] = useState(false);
    // Everything else recorded near this attempt. See related-operations-target.md.
    const [showRelated, setShowRelated] = useState(false);

    // Which version of the question to render. Defaults to the one the grader saw —
    // the score, feedback and student work on this page all refer to that version.
    const [showLiveQuestion, setShowLiveQuestion] = useState(false);

    // Detect-learning-moments state. The result is worth keeping on screen:
    // the whole point of the button is seeing WHICH moments fired, and whether
    // a freshly authored detectionHint caught the case it was written for.
    const [detectStatus, setDetectStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [detectResult, setDetectResult] = useState<DetectLearningMomentsResult | null>(null);
    const [detectError, setDetectError] = useState<string | null>(null);
    const [secondReadStatus, setSecondReadStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [secondReadError, setSecondReadError] = useState<string | null>(null);
    const [secondReadSkipped, setSecondReadSkipped] = useState<string | null>(null);
    // The stored read shows only when asked for; the button's colour carries the verdict.
    const [secondReadOpen, setSecondReadOpen] = useState(false);

    // Re-evaluate state
    const [reEvalStatus, setReEvalStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [reEvalError, setReEvalError] = useState<string | null>(null);
    const [showReEvalDialog, setShowReEvalDialog] = useState(false);

    // Track mount time so refetchInterval can give up after POLL_MAX_MS while
    // a submission is stuck in `status='analyzing'`.
    const mountedAtRef = useRef<number>(Date.now());

    const { data: submission, isLoading, error, refetch } = useQuery({
        queryKey: ['submission', id],
        queryFn: () => adminApi.getSubmission(id!),
        enabled: !!id,
        refetchInterval: (query) => {
            const status = query.state.data?.status;
            const elapsed = Date.now() - mountedAtRef.current;
            return status === 'analyzing' && elapsed < POLL_MAX_MS ? POLL_INTERVAL_MS : false;
        },
    });

    const { data: modelRuns } = useQuery({
        queryKey: ['modelRuns', id],
        queryFn: () => adminApi.getModelRuns(id!),
        enabled: !!id,
        // Rows exist from each call's start; refresh while one is in flight.
        refetchInterval: (q) => (q.state.data?.some(r => r.status === 'running') ? 5000 : false),
    });

    const { data: prompts } = useQuery({
        queryKey: ['prompts', 'includeArchived'],
        queryFn: () => adminApi.getPrompts({ includeArchived: true }),
    });

    const { getPromptName, getPromptType } = usePromptLookup(prompts);

    const rerunMutation = useMutation({
        mutationFn: ({ pipelineConfig, snapshotMode }: { pipelineConfig: string; snapshotMode: 'latest' | 'original' }) =>
            adminApi.rerunSubmission(id!, undefined, undefined, undefined, undefined, pipelineConfig, snapshotMode),
        onMutate: () => {
            setRerunStatus('running');
            setRerunError(null);
            setRerunResult(null);
            setShowRerunDialog(false);
        },
        onSuccess: (data) => {
            setRerunStatus('success');
            setRerunResult({ newSubmissionId: data.newSubmissionId });
        },
        onError: (err) => {
            setRerunStatus('error');
            setRerunError(err instanceof Error ? err.message : 'Failed to rerun submission');
        },
    });

    const detectMutation = useMutation({
        mutationFn: () => adminApi.detectLearningMoments(id!),
        onMutate: () => {
            setDetectStatus('loading');
            setDetectError(null);
            setDetectResult(null);
        },
        onSuccess: (data) => {
            setDetectStatus('success');
            setDetectResult(data);
        },
        onError: (err) => {
            setDetectStatus('error');
            setDetectError(err instanceof Error ? err.message : 'Failed to detect learning moments');
        },
    });

    const secondReadMutation = useMutation({
        mutationFn: () => adminApi.secondRead(id!),
        onMutate: () => {
            setSecondReadStatus('loading');
            setSecondReadError(null);
            setSecondReadSkipped(null);
        },
        onSuccess: (data) => {
            setSecondReadStatus('success');
            setSecondReadOpen(true);
            if (data.status === 'skipped') setSecondReadSkipped(data.reason ?? 'skipped');
            // The summary is stored on the analysis; refetch to show it.
            queryClient.invalidateQueries({ queryKey: ['submission', id] });
            queryClient.invalidateQueries({ queryKey: ['modelRuns', id] });
        },
        onError: (err) => {
            setSecondReadStatus('error');
            setSecondReadError(err instanceof Error ? err.message : 'Second read failed');
        },
    });

    const reEvalMutation = useMutation({
        mutationFn: ({ note, why }: { note?: string; why?: AdjustmentWhyValue }) => adminApi.reEvaluate(id!, note, why),
        onMutate: () => {
            setReEvalStatus('loading');
            setReEvalError(null);
            setShowReEvalDialog(false);
        },
        onSuccess: () => {
            setReEvalStatus('success');
            queryClient.invalidateQueries({ queryKey: ['submission', id] });
            queryClient.invalidateQueries({ queryKey: ['modelRuns', id] });
        },
        onError: (err) => {
            setReEvalStatus('error');
            setReEvalError(err instanceof Error ? err.message : 'Re-evaluation failed');
        },
    });

    useEffect(() => {
        if (modelRuns && modelRuns.length > 0 && !selectedRunId) {
            // The primary whose result was recorded — not simply the first
            // primary, which on a submission a timed-out attempt also wrote to
            // opened the abandoned run. See supersededRuns.ts.
            const { activePrimaryId } = classifySupersededRuns(modelRuns);
            setSelectedRunId(activePrimaryId ?? modelRuns[0].id);
        }
    }, [modelRuns]);

    if (isLoading) return <div className="loading">Loading...</div>;
    if (error) return <div className="error">Error: {(error as Error).message}</div>;
    if (!submission) return <div className="error">Submission not found</div>;

    const elapsedMs = Date.now() - mountedAtRef.current;
    const pollingExhausted = submission.status === 'analyzing' && elapsedMs >= POLL_MAX_MS;

    if (submission.status === 'analyzing') {
        return (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
                <h2>Analysis in progress…</h2>
                <p>This submission is still being analyzed. The page will refresh automatically.</p>
                <p style={{ color: '#888', fontSize: '0.9rem' }}>
                    Submission: <code>{submission.id}</code>
                </p>
                {pollingExhausted && (
                    <>
                        <p style={{ color: '#888', marginTop: '1rem' }}>
                            Auto-refresh stopped after {Math.round(POLL_MAX_MS / 60_000)} minutes — analysis may be stuck.
                        </p>
                        <button type="button" style={{ marginTop: '0.5rem' }} onClick={() => { mountedAtRef.current = Date.now(); refetch(); }}>
                            Refresh
                        </button>
                    </>
                )}
            </div>
        );
    }

    const isFailed = submission.status === 'failed';
    const selectedRun = modelRuns?.find(r => r.id === selectedRunId);
    // A step the pipeline declined to run. It made no LLM call, so it is NOT a
    // failing model run — telling the reader to go find one sends them looking
    // for a row that cannot exist. Name the step and the reason instead.
    const skippedRuns = (modelRuns ?? []).filter(r => r.status === 'skipped');
    const isMCQWithoutWorking = submission.selectedOption && !submission.canvasImageUrl && !submission.typedText;
    const rerunDisabled = rerunStatus === 'running' || !!isMCQWithoutWorking;

    // Per-pipeline step totals: max(stepIndex)+1 within each pipelineName.
    // Per (pipelineName, stepIndex) attempts: sort by generatedAt so the
    // earliest attempt is #1, subsequent ones are retries.
    const pipelineStepTotals = new Map<string, number>();
    const stepAttempts = new Map<string, { id: string; t: number }[]>();
    for (const r of modelRuns ?? []) {
        const pipe = r.pipelineName ?? '';
        if (r.stepIndex !== undefined) {
            pipelineStepTotals.set(pipe, Math.max(pipelineStepTotals.get(pipe) ?? 0, r.stepIndex + 1));
        }
        const key = `${pipe}::${r.stepIndex ?? '?'}`;
        const list = stepAttempts.get(key) ?? [];
        list.push({ id: r.id, t: new Date(r.generatedAt).getTime() });
        stepAttempts.set(key, list);
    }
    for (const list of stepAttempts.values()) {
        list.sort((a, b) => a.t - b.t || a.id.localeCompare(b.id));
    }
    const supersession = classifySupersededRuns(modelRuns ?? []);
    const attemptIndexFor = (r: ModelOutput) => {
        const key = `${r.pipelineName ?? ''}::${r.stepIndex ?? '?'}`;
        const list = stepAttempts.get(key) ?? [];
        const idx = list.findIndex(x => x.id === r.id);
        return { attempt: idx + 1, total: list.length };
    };

    // Tier the attempt ran at (winner = highest tier reached), used to order a
    // step's attempts Tier1 → Tier2 → Tier3 top-to-bottom. Non-tiered rows rank
    // 0 (they don't share a step with tiered attempts in practice).
    const tierRank = (r: ModelOutput): number => {
        const m = /^Tier(\d+)$/i.exec(r.parameters.actual?.tierRouting?.finalTier ?? '');
        return m ? parseInt(m[1], 10) : 0;
    };

    // Display order: pipeline → step → tier → attempt. Tier ordering makes the
    // escalation chain read in sequence; generatedAt is the final tiebreak (and
    // still drives the retry-badge numbering, which is computed separately).
    // (Execution order is unaffected — parallel-set branches still run
    // concurrently; this is presentation only.)
    const sortedRuns = [...(modelRuns ?? [])].sort((a, b) =>
        (a.pipelineName ?? '').localeCompare(b.pipelineName ?? '')
        || (a.stepIndex ?? 0) - (b.stepIndex ?? 0)
        || tierRank(a) - tierRank(b)
        || new Date(a.generatedAt).getTime() - new Date(b.generatedAt).getTime()
        || a.id.localeCompare(b.id),
    );

    // Pipeline config that ran for this submission — the resolved name
    // (user override → else the active default config name) stamped on the
    // submission doc at create time and surfaced here via user.pipelineConfig.
    // Undefined only on legacy submissions created before this was resolved →
    // shown as "—" (no fabricated "default"). The individual pipeline NAMES
    // that ran (there can be several — parallel/shadow pipelines within the
    // one config) are shown per-row in the Prompts Executed table.
    const pipelineConfigUsed = submission.user?.pipelineConfig || '—';

    // The question this attempt was actually graded against, and the one on screen.
    // Snapshot leads; the live doc is the fallback (no snapshot) and the alternate view
    // (drifted, and the reader asked to see today's version).
    const attemptedQuestion = submission.questionAsAttempted ?? null;
    const liveQuestion = submission.question;
    const shownQuestion = (!attemptedQuestion || showLiveQuestion) ? liveQuestion : attemptedQuestion;

    // Shared run columns by name (cols.prompt / type / provider / latency /
    // cost / tokens / output). We reference them by key — never by array
    // position — and arrange the final order explicitly below, splicing in the
    // submission-specific columns (Pipeline, Step, Score) that don't apply to
    // the Hint / Concept Lab pages.
    const cols = buildRunColumnMap<ModelOutput>(
        { getPromptName, getPromptType },
        { returnTo: { label: 'Solution Analysis' } },
    );
    const stepColumn: RunsTableColumn<ModelOutput> = {
        header: 'Step',
        className: 'step-col',
        render: r => {
            const pipe = r.pipelineName;
            if (!pipe && r.stepIndex === undefined) return '-';
            const total = pipelineStepTotals.get(pipe ?? '') ?? 1;
            const stepNum = (r.stepIndex ?? 0) + 1;
            const { attempt, total: attemptsAtStep } = attemptIndexFor(r);

            // Routing-aware badges: tier-escalation and parallel-branch runs
            // are concurrent/policy re-fires, not retries — label them from
            // their routing traces. "Retry N" remains only for true repeat
            // attempts with no routing trace (e.g. admin reruns).
            const tr = r.parameters.actual?.tierRouting;
            const pr = r.parameters.actual?.parallelRouting;
            const badges: React.ReactNode[] = [];
            if (tr) {
                if (tr.rejectedByEscalation) {
                    // Truncated trace: finalTier = the tier this attempt ran at;
                    // the last escalation entry says why it was escalated away.
                    const why = triggerLabel(tr.escalations[tr.escalations.length - 1]?.triggeredBy);
                    badges.push(
                        <span key="tier" className="retry-badge tier-badge rejected" title={`Ran at ${tr.finalTier} (set: ${tr.providerSet}); escalated away${why ? ` — trigger: ${why}` : ''}`}>
                            ↑ {tr.finalTier} → escalated{why ? ` (${why})` : ''}
                        </span>,
                    );
                } else if (tr.escalations.length > 0) {
                    // Final served attempt after escalation: show only the tier it
                    // landed on. The full hop chain stays in the hover tooltip; the
                    // per-tier rows above (Tier1 → escalated, Tier2 → escalated)
                    // show the path in order.
                    badges.push(
                        <span key="tier" className="retry-badge tier-badge" title={`Escalated ${tr.escalations.map(e => `${e.fromTier}→${e.toTier} (${triggerLabel(e.triggeredBy)})`).join(', ')} (set: ${tr.providerSet})`}>
                            ↑ {tr.finalTier}
                        </span>,
                    );
                } else {
                    badges.push(
                        <span key="tier" className="retry-badge tier-badge plain" title={`Tier-routed (set: ${tr.providerSet}), no escalation`}>
                            {tr.finalTier}
                        </span>,
                    );
                }
            }
            if (pr) {
                const reason = pr.branches.find(b => b.branch !== pr.winnerBranch && b.rejected)?.rejectionReason;
                badges.push(
                    pr.rejectedByParallel ? (
                        <span key="branch" className="retry-badge branch-badge rejected" title={`Parallel set ${pr.parallelSet} (${pr.selection}); winner: ${pr.winnerBranch}${reason ? `; reason: ${reason}` : ''}`}>
                            ⑂ branch — not selected
                        </span>
                    ) : (
                        <span key="branch" className="retry-badge branch-badge" title={`Parallel set ${pr.parallelSet} (${pr.selection}) — selected over ${pr.branches.length - 1} other branch(es)`}>
                            ⑂ branch — winner
                        </span>
                    ),
                );
            }
            // Ahead of the retry badge: a row from a superseded run is not a
            // retry of anything, it is the run the retry replaced.
            const superseded = supersession.reasonFor(r);
            if (superseded) badges.push(<SupersededBadge key="superseded" reason={superseded} />);
            if (badges.length === 0 && attemptsAtStep > 1) {
                badges.push(
                    <span key="retry" className="retry-badge" title={`Attempt ${attempt} of ${attemptsAtStep} at this step`}>
                        ↻ Retry {attempt}
                    </span>,
                );
            }

            return (
                <span className="step-cell">
                    <span className="step-position">Step {stepNum}/{total}</span>
                    {badges}
                    {/* Student solution image attached to THIS call. Question
                        diagrams and page scans don't count — only the student's
                        own work. */}
                    {sentStudentImage(r.attachedImages) && (() => {
                        const label = studentImageLabel(r.attachedImages);
                        return (
                            <span
                                className="retry-badge img-badge"
                                title={label
                                    ? `Student solution image attached to this call, labeled "${label}"`
                                    : 'Student solution image attached to this call'}
                            >
                                🖼 image{label ? '*' : ''}
                            </span>
                        );
                    })()}
                </span>
            );
        },
    };
    const scoreColumn: RunsTableColumn<ModelOutput> = {
        header: 'Score',
        render: r => {
            if (getPromptType(r.parameters.promptId) === 'extract_steps') return '-';
            if (r.errors?.length) return <span className="error-badge">Error</span>;
            const score = r.parsedAssessment?.score;
            return <span className={`score ${getScoreClass(score)}`}>{score ?? '-'}</span>;
        },
    };
    // Pipeline name as its own leading column (moved out of the Step cell).
    const pipelineColumn: RunsTableColumn<ModelOutput> = {
        header: 'Pipeline',
        render: r => r.pipelineName
            ? <span className="pipeline-name">{r.pipelineName}</span>
            : <span className="muted">-</span>,
    };
    // Merge the shared Prompt (name + open-editor link) and Type (badge) cells
    // into one "Prompt (Type)" column by composing their renders.
    const promptTypeColumn: RunsTableColumn<ModelOutput> = {
        header: 'Prompt (Type)',
        render: r => (
            <div className="prompt-type-cell">
                {cols.prompt.render(r)}
                {cols.type.render(r)}
            </div>
        ),
        // Sorts on the type, matching the shared Type column this absorbed —
        // grouping by prompt type is the reason to sort this column at all.
        sortValue: cols.type.sortValue,
    };
    // Provider cell + the original provider-set name in brackets (from the
    // tier/parallel routing trace). Reuses the shared ProviderCell render.
    const providerColumn: RunsTableColumn<ModelOutput> = {
        // "Model" — the cell renders params.model; see the shared ProviderCell.
        header: 'Model',
        render: r => {
            const set = r.parameters.actual?.tierRouting?.providerSet
                ?? r.parameters.actual?.parallelRouting?.parallelSet;
            return (
                <span className="provider-with-set">
                    {cols.provider.render(r)}
                    {set && <span className="provider-set-name">({set})</span>}
                </span>
            );
        },
        sortValue: cols.provider.sortValue,
    };
    // Output column with a width class so the id + link icon stay on one row.
    const outputColumn: RunsTableColumn<ModelOutput> = { ...cols.output, className: 'output-col' };

    // Order: Time, Pipeline, Prompt (Type), Model, Step, Score, Latency, Cost,
    // Tokens, Output, Status. Time leads for the same reason it does in the
    // shared default order — nothing else in the table shows execution order.
    const runColumns: RunsTableColumn<ModelOutput>[] = [
        cols.time,
        pipelineColumn,
        promptTypeColumn,
        providerColumn,
        stepColumn,
        scoreColumn,
        cols.latency,
        cols.cost,
        cols.tokens,
        outputColumn,
        cols.status,
    ];

    const headerActions = (
        <>
            <button
                className={`rerun-button${rerunStatus === 'running' ? ' running' : ''}`}
                onClick={() => setShowRerunDialog(true)}
                disabled={rerunDisabled}
                title={isMCQWithoutWorking ? 'MCQ without working - nothing to re-analyze' : undefined}
            >
                {rerunStatus === 'running' ? 'Rerunning...' : 'Rerun'}
            </button>
            {rerunStatus === 'success' && rerunResult && (
                <Link to={`/submissions/${rerunResult.newSubmissionId}`} className="new-submission-link">
                    View New Submission
                </Link>
            )}
            <span className="header-divider">|</span>
            <button
                className="playground-link-button"
                onClick={() => navigate('/submissions', { state: { openPlayground: true, questionId: submission.questionId, submissionId: submission.id } })}
                title="Experiment with prompts using this submission's context"
            >
                Playground
            </button>
            <span className="header-divider">|</span>
            <AddToBenchButton submissionId={submission.id} />
            <span className="header-divider">|</span>
            <button
                className={`reeval-button${reEvalStatus === 'loading' ? ' is-loading' : ''}`}
                onClick={() => setShowReEvalDialog(true)}
                disabled={reEvalStatus === 'loading'}
            >
                {reEvalStatus === 'loading' ? 'Re-evaluating...' : 'Re-evaluate'}
            </button>
            {reEvalStatus === 'success' && <span className="reeval-success">Score updated</span>}
            <span className="header-divider">|</span>
            <button
                className="reeval-button"
                onClick={() => setShowRelated(true)}
                title="Reruns, re-evaluations, the split, the learning moments, and this student's other attempts at this question."
            >
                Related
            </button>
            <span className="header-divider">|</span>
            <button
                className={`detect-lm-button${detectStatus === 'loading' ? ' is-loading' : ''}`}
                onClick={() => detectMutation.mutate()}
                disabled={detectStatus === 'loading'}
                title="Re-run learning-moment detection on this submission. Replaces this submission's existing encounters rather than adding to them."
            >
                {detectStatus === 'loading' ? 'Detecting...' : 'Detect Learning Moments'}
            </button>
            <span className="header-divider">|</span>
            <button
                className={`detect-lm-button${secondReadStatus === 'loading' ? ' is-loading' : ''}${submission.answerSecondRead ? ` second-read-button--${secondReadTone(submission.answerSecondRead)}` : ''}`}
                onClick={() => (submission.answerSecondRead ? setSecondReadOpen(o => !o) : secondReadMutation.mutate())}
                disabled={secondReadStatus === 'loading'}
                aria-expanded={submission.answerSecondRead ? secondReadOpen : undefined}
                title={submission.answerSecondRead
                    ? 'Green: the second read agrees with the answer the analysis served. Red: it disagrees. Yellow: undecided, or the question was not found on the pages. Click for details.'
                    : 'Read ONLY the final answer off the uncropped uploaded page, independently of the split and extract_visuals and blind to the key. Observation only — changes no score.'}
            >
                {secondReadStatus === 'loading' ? 'Reading...' : 'Second read'}
                {submission.answerSecondRead && secondReadStatus !== 'loading' && (secondReadOpen ? ' ▾' : ' ▸')}
            </button>
        </>
    );

    const actionStatus = (
        <>
            {rerunStatus === 'error' && rerunError && <div className="header-action-error">{rerunError}</div>}
            {reEvalStatus === 'error' && reEvalError && <div className="header-action-error">{reEvalError}</div>}
            {detectStatus === 'error' && detectError && <div className="header-action-error">{detectError}</div>}
            {secondReadStatus === 'error' && secondReadError && <div className="header-action-error">{secondReadError}</div>}
            {secondReadSkipped && <div className="header-action-error">Second read skipped: {secondReadSkipped}</div>}
            {submission.answerSecondRead && secondReadOpen && (
                <SecondReadPanel
                    read={submission.answerSecondRead}
                    onReread={() => secondReadMutation.mutate()}
                    rereading={secondReadStatus === 'loading'}
                />
            )}
            {detectStatus === 'success' && detectResult && (
                <div className="detect-lm-result">
                    <strong>
                        {detectResult.detected.length === 0
                            ? 'No learning moments detected'
                            : `${detectResult.detected.length} learning moment${detectResult.detected.length === 1 ? '' : 's'} detected`}
                    </strong>
                    {detectResult.clearedEncounters > 0 && (
                        <span className="detect-lm-cleared"> · replaced {detectResult.clearedEncounters} earlier encounter{detectResult.clearedEncounters === 1 ? '' : 's'}</span>
                    )}
                    <ul>
                        {detectResult.detected.map((d, i) => (
                            <li key={`${d.learningMomentId}-${i}`}>
                                <span className={`detect-lm-flag detect-lm-flag--${d.wasPositive ? 'applied' : 'missed'}`}>
                                    {d.wasPositive ? 'applied' : 'missed'}
                                </span>
                                <span className="detect-lm-name">{d.momentName}</span>
                                <span className="detect-lm-context">{d.context}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </>
    );

    // Selected-run sub-components (per-section).
    const selectedPromptType = selectedRun ? getPromptType(selectedRun.parameters.promptId) : undefined;
    const selectedRunRawResponse = selectedRun ? getRawOutputText(selectedRun) : '';
    // Error banner text — lead with the structured error code (taxonomy root
    // cause) so e.g. a truncation reads as [max_tokens], not as a parse bug.
    // The last errorRecord is the most recent stage (parse after call). New rows
    // persist only errorRecords (no legacy `errors[]`), so fall back to the
    // record's message — otherwise the banner silently disappears.
    const selectedRunError = (() => {
        if (!selectedRun) return undefined;
        const records = selectedRun.errorRecords;
        const last = records?.[records.length - 1];
        const msg = selectedRun.errors?.[0] ?? last?.message;
        if (!msg) return undefined;
        return last?.code ? `[${last.code}] ${msg}` : msg;
    })();
    const runSummary = selectedRun && (
        <RunSummary run={selectedRun} promptType={selectedPromptType} />
    );
    // Skip the panel when the run produced nothing parseable. Otherwise wrap
    // the shared schema-aware body with this page's collapsible + quality-check
    // badges decoration.
    // Parsed body + quality badges are passed as slots to the shared
    // ModelOutputView (via RunDetailCard) — it owns the collapsible wrapper.
    const parsedFeedbackHasContent = !!(selectedRun && (selectedRun.parsedAssessment || selectedRun.extractedData || selectedRunRawResponse));
    const parsedFeedbackBody = selectedRun && parsedFeedbackHasContent ? (
        <ParsedFeedbackBody
            promptType={selectedPromptType}
            feedback={selectedRun.parsedAssessment}
            extractedData={selectedRun.extractedData}
            rawResponse={selectedRunRawResponse}
            question={submission.question}
            learningMoments={submission.learningMoments}
            onRejectMoment={async (learningMomentId, rejected, comment, why) => {
                await adminApi.rejectLearningMoment(submission.id, learningMomentId, rejected, comment, why);
                await queryClient.invalidateQueries({ queryKey: ['submission', id] });
            }}
            sentStudentImages={sentStudentImagesOf(
                selectedRun.attachedImages, submission.canvasImageUrl,
                (submission as { solutionPageUrls?: string[] }).solutionPageUrls,
            )}
        />
    ) : undefined;
    const runQualityBadges = selectedRun ? <QualityCheckBadges qualityChecks={selectedRun.qualityChecks} /> : undefined;
    const runDetailHeading = selectedRun && (
        <RunDetailHeading
            run={selectedRun}
            promptName={getPromptName(selectedRun.parameters.promptId)}
            promptType={selectedPromptType}
        />
    );

    return (
        <>
            <OperationDetailPage
                title="Solution Analysis Details"
                id={submission.id}
                idContainer="submissions"
                backLabel={navState?.backLabel ?? 'Operations'}
                navSlot={opNav && <OperationNavStrip nav={opNav} noun="Analysis" />}
                onBack={() => navigate(navState?.backTo ?? (backSearch ? `/submissions?${backSearch}` : '/submissions'))}
                titleRight={<ItemTagActions itemType="submission" itemId={submission.id} />}
                actions={headerActions}
                actionStatus={actionStatus}
                context={<>
                    {isFailed && (
                        skippedRuns.length > 0 ? (
                            <ErrorBanner title="Analysis failed — a pipeline step was skipped.">
                                {skippedRuns.map(r => (
                                    <div key={r.id}>
                                        <strong>{r.stepType ?? `step ${(r.stepIndex ?? 0) + 1}`}</strong>
                                        {' was skipped, so the pipeline produced no result. '}
                                        {r.skipReason}
                                    </div>
                                ))}
                                <div style={{ marginTop: '0.4rem', opacity: 0.85 }}>
                                    A skipped step makes no model call — it appears in Model Runs below as
                                    {' '}<em>skipped</em>, with no cost or latency.
                                </div>
                            </ErrorBanner>
                        ) : (
                            <ErrorBanner title="Analysis failed.">
                                The pipeline didn't complete for this submission — see model runs below for the failing step.
                            </ErrorBanner>
                        )
                    )}
                    <ContextGrid
                        left={
                            <UserProfileCard
                                user={{
                                    id: submission.user?.id,
                                    name: submission.user?.name,
                                    email: submission.user?.email,
                                    grade: submission.user?.grade,
                                    // Pipeline config is shown in the Question & Result
                                    // card (MetaItem) — omit here to avoid duplication.
                                }}
                                onViewUser={uid => navigate(`/users/${uid}`)}
                            >
                                <DetailItem label="Proficiency">
                                    {submission.user?.subjectProficiency ? (
                                        <span className={`proficiency-badge ${submission.user.subjectProficiency.level}`}>
                                            {submission.user.subjectProficiency.level}
                                        </span>
                                    ) : (
                                        <span className="proficiency-badge medium">Medium</span>
                                    )}
                                </DetailItem>
                                <ClientInfoItems clientInfo={submission.clientInfo} />
                            </UserProfileCard>
                        }
                        right={
                            <MetadataCard title="Question & Result">
                                <MetaItem label="Pipeline Config"><span className="tag">{pipelineConfigUsed}</span></MetaItem>
                                <MetaItem label="Type"><span className="tag">{shownQuestion?.questionType || submission.question?.questionType || '-'}</span></MetaItem>
                                <MetaItem label="Target Exam"><span className="tag">{submission.targetExamForSubmission || '-'}</span></MetaItem>
                                <MetaItem label="Time Taken">{formatTime(submission.timeTakenSeconds)}</MetaItem>
                                <MetaItem label="Method">
                                    {submission.attemptSignals?.method
                                        ? <span className="tag">{submission.attemptSignals.method}</span>
                                        : <span className="muted">—</span>}
                                </MetaItem>
                                <MetaItem label="Broke At">
                                    {submission.attemptSignals?.firstWrongStep
                                        // Step 1 means it broke before any of the student's own
                                        // working — the read of the question, not the maths.
                                        ? <span className="tag">
                                            step {submission.attemptSignals.firstWrongStep}
                                            {submission.attemptSignals.stepCount ? ` of ${submission.attemptSignals.stepCount}` : ''}
                                          </span>
                                        : submission.attemptSignals?.stepCount
                                            ? <span className="muted">no wrong step ({submission.attemptSignals.stepCount})</span>
                                            : <span className="muted">—</span>}
                                </MetaItem>
                                <MetaItem label="Diagnosis">
                                    {/* Vocabulary 2: the kind and stage ARE the finding; the
                                        category is only derived from them. */}
                                    {submission.attemptSignals?.diagnosisVocabularyVersion === 2 && submission.attemptSignals.diagnosisKind
                                        ? <span className="tag">
                                            {submission.attemptSignals.diagnosisKind.replace(/_/g, ' ')}
                                            {submission.attemptSignals.diagnosisStage ? ` · ${submission.attemptSignals.diagnosisStage.replace(/_/g, ' ')}` : ''}
                                            {submission.attemptSignals.diagnosisCertainty === 'tentative' ? ' · tentative' : ''}
                                          </span>
                                        : submission.attemptSignals?.diagnosisCategory
                                        ? <span className="tag">
                                            {submission.attemptSignals.diagnosisCategory.replace(/_/g, ' ')}
                                            {submission.attemptSignals.diagnosisKind && submission.attemptSignals.diagnosisKind !== 'none'
                                                ? ` · ${submission.attemptSignals.diagnosisKind}` : ''}
                                          </span>
                                        : <span className="muted">—</span>}
                                </MetaItem>
                                <MetaItem label="Score" className="score-item">
                                    <span className={`score ${getScoreClass(submission.feedback?.score)}`}>
                                        {submission.feedback?.score ?? '-'}/10
                                    </span>
                                </MetaItem>
                                <MetaItem label="Submitted">
                                    <span className="datetime">{format(new Date(submission.submittedAt), 'MMM dd, HH:mm:ss')}</span>
                                </MetaItem>
                            </MetadataCard>
                        }
                    />
                </>}
                input={
                    <>
                        <QuestionCard
                            question={{
                                id: submission.questionId,
                                // The paper's number, from the live doc: the snapshot
                                // does not carry it, and an edit never renumbers.
                                sourceQuestionNumber: liveQuestion?.sourceQuestionNumber,
                                questionText: shownQuestion?.questionText,
                                questionDiagramUrl: shownQuestion?.questionDiagramUrl,
                                figures: shownQuestion?.figures,
                                questionType: shownQuestion?.questionType,
                                parts: shownQuestion?.parts,
                                correctAnswer: shownQuestion?.correctAnswer,
                                // Not part of the snapshot and not what the score turned on —
                                // the reference solution image always comes from the live doc.
                                solutionImageUrl: submission.question?.solutionImageUrl,
                                options: shownQuestion?.options,
                            }}
                            onViewQuestion={qid => navigate(`/questions?id=${qid}`)}
                            showAnswer
                            // What the analysis read off the student's work. Shown next to the
                            // correct answer because a wrong score is usually a bad extraction,
                            // and that was only visible further down under "Answers Detected".
                            studentAnswer={submission.feedback?.llmAssessment?.studentLlmAnswer}
                            banner={
                                <QuestionVersionBanner
                                    hasSnapshot={!!submission.questionAsAttempted}
                                    drift={submission.questionDriftFields ?? []}
                                    showingLive={showLiveQuestion}
                                    onToggle={() => setShowLiveQuestion(v => !v)}
                                />
                            }
                        />
                        {/* The snapshot carries no solutions — like the solution image, they come from the live doc. */}
                        <GivenSolutionCard solutions={submission.question?.solutions} />
                        <StudentWorkCard
                            work={submission}
                            /* The student picked from THESE options — resolving their choice
                               against a since-edited list would mislabel it. */
                            questionOptions={attemptedQuestion?.options ?? submission.question?.options}
                            onReplay={submission.canvasImageUrl ? () => setShowReplayModal(true) : undefined}
                            /* A wrong SCORE is what Re-evaluate fixes. This fixes a wrong
                               READING — the work shown being another question's, which
                               re-evaluating would only read again. Offered when the
                               submission names the attempt whose pages it came from. */
                            actions={submission.testSessionId ? (
                                <button
                                    className="replay-link"
                                    onClick={() => setCorrecting(true)}
                                    title="The work shown belongs to a different question — point us at the right region on the uploaded pages and re-mark against it."
                                >
                                    Correct Region
                                </button>
                            ) : undefined}
                        />
                        {/* Beneath the page it is read from: a reviewer settling a
                            doubtful reading looks up at the work, then answers. */}
                        <EvidencePanel
                            submissionId={submission.id}
                            evidence={submission.evidence}
                            readers={readerVersionsOf(submission)}
                            onSettled={() => refetch()}
                            onReEvaluate={() => setShowReEvalDialog(true)}
                        />
                    </>
                }
                // Describes the analysis as a whole, across its steps — which is
                // exactly what this slot is for, and why it sits above the runs
                // table rather than inside any one run.
                operationSummary={<AnalysisJudgeVerdicts submissionId={submission.id} />}
                runs={<>
                    {modelRuns && modelRuns.length > 0 && (
                    <RunsTable
                        rows={sortedRuns}
                        columns={runColumns}
                        rowKey={r => r.id}
                        selectedRowId={selectedRunId}
                        onSelect={r => setSelectedRunId(r.id)}
                        getRowClass={r => [
                            supersession.reasonFor(r) ? 'superseded' : r.isPrimary ? 'primary' : '',
                            r.errors?.length ? 'has-error' : '',
                        ].filter(Boolean).join(' ')}
                    />
                    )}
                </>}
                runDetail={selectedRun && (
                    <RunDetailCard
                        heading={runDetailHeading}
                        error={selectedRunError}
                        errorRecords={selectedRun.errorRecords}
                        summary={runSummary}
                        systemPrompt={selectedRun.systemPromptText}
                        userPrompt={selectedRun.promptText}
                        rawResponse={selectedRunRawResponse}
                        rawOutputFull={selectedRun.rawOutputFull}
                        reasoningSummary={selectedRun.reasoningSummaryText}
                        qualityBadges={runQualityBadges}
                        modelInputId={selectedRun.inputId}
                        modelOutputId={selectedRun.id}
                        parsed={parsedFeedbackBody}
                    >
                        <AdminFeedbackPanel submissionId={id!} userFeedback={submission.userFeedback} />
                        <ScoreOverridePanel submissionId={id!} />
                    </RunDetailCard>
                )}
            />

            {showRerunDialog && (
                <RerunDialog
                    onClose={() => setShowRerunDialog(false)}
                    onRun={(req) => rerunMutation.mutate(req)}
                />
            )}

            {showReEvalDialog && (
                <ReEvaluateDialog
                    onClose={() => setShowReEvalDialog(false)}
                    onRun={(note, why) => reEvalMutation.mutate({ note, why })}
                />
            )}

            {/* Canvas replay modal */}
            {showReplayModal && (
                <CanvasReplayModal submissionId={id!} onClose={() => setShowReplayModal(false)} />
            )}

            {showRelated && (
                <RelatedOperationsDialog submissionId={id!} onClose={() => setShowRelated(false)} />
            )}

            {correcting && submission.testSessionId && (
                <RegionCorrector
                    questions={[{
                        questionId: submission.questionId,
                        printedNumber: liveQuestion?.sourceQuestionNumber,
                        questionText: liveQuestion?.questionText ?? '',
                    }]}
                    initialQuestionId={submission.questionId}
                    resolveUpload={() => correctionApi(submission.userId)
                        .resolveUpload(submission.testSessionId!)}
                    // Whose side, and a note for our records — see analysis-adjustments-target.md §4e.
                    askWhy
                    correctRegion={input => correctionApi(submission.userId)
                        .correctRegion(submission.testSessionId!, input)}
                    onClose={() => setCorrecting(false)}
                    // The score on this page has just been re-derived from a
                    // different image, so nothing on it is current any more.
                    onCorrected={() => refetch()}
                />
            )}
        </>
    );
}

const DRIFT_LABELS: Record<string, string> = {
    questionText: 'question text',
    correctAnswer: 'correct answer',
    options: 'options',
    questionDiagramUrl: 'diagram',
    // The core hash caught a change outside the four fields this page renders —
    // a part, the choice requirement, the marking rubric, the mark total.
    other: 'parts or marking',
};

/**
 * Says which version of the question is on screen.
 *
 * The score, feedback and student work below were produced against the question as it was
 * when the analysis ran, so that version leads. Three states worth distinguishing:
 * drifted (say what changed, offer today's version), snapshot-and-unchanged (stay quiet —
 * a banner on every clean attempt would be noise), and no snapshot at all, where the live
 * question is the only thing to show and the reader must be told that is what they're
 * reading.
 */
function QuestionVersionBanner({ hasSnapshot, drift, showingLive, onToggle }: {
    hasSnapshot: boolean;
    drift: string[];
    showingLive: boolean;
    onToggle: () => void;
}) {
    if (!hasSnapshot) {
        return (
            <div className="question-version-note" data-testid="question-version-no-snapshot">
                No snapshot for this attempt — showing the question as it stands today, which
                may not be what was graded.
            </div>
        );
    }
    if (drift.length === 0) return null;

    const changed = drift.map(f => DRIFT_LABELS[f] ?? f).join(', ');
    return (
        <div className="question-version-warn" data-testid="question-version-drift">
            <span>
                {showingLive
                    ? `Showing the CURRENT question. It was edited after this attempt (${changed}) — the score below was not produced against it.`
                    : `Edited after this attempt (${changed}). Showing the version that was graded.`}
            </span>
            <button type="button" className="btn-link" onClick={onToggle}>
                {showingLive ? 'Show version graded' : 'Show current version'}
            </button>
        </div>
    );
}

/** A run whose result is not the one recorded — see supersededRuns.ts. */
function SupersededBadge({ reason }: { reason: SupersededReason }) {
    const attempt = reason.jobAttempt !== undefined ? ` · attempt ${reason.jobAttempt}` : '';
    return reason.kind === 'abandoned' ? (
        <span className="retry-badge superseded-badge"
            title="The job runner timed this attempt out before its calls came back. The calls are kept here because they ran and were paid for; its result was not recorded — a retry's was.">
            ⏱ timed out{attempt}
        </span>
    ) : (
        <span className="retry-badge superseded-badge"
            title="This run also saved a primary output, but a newer run's result is the one recorded. Most likely an analysis attempt the job runner timed out that finished and saved anyway, from before such attempts were marked.">
            superseded{attempt}
        </span>
    );
}

/** The answer second read beside what the analysis served. Observation only.
 *  See docs/FeatureDesigns/solution-analysis/answer-second-read-target.md. */
/** Green when the second read agrees with what was served, red when it does not,
 *  yellow when it could not say — including a question it did not find. */
function secondReadTone(read: AnswerSecondRead): 'agree' | 'disagree' | 'unsure' {
    if (!read.found) return 'unsure';
    return read.agreesWithServed === 'yes' ? 'agree' : read.agreesWithServed === 'no' ? 'disagree' : 'unsure';
}

function SecondReadPanel({ read, onReread, rereading }: { read: AnswerSecondRead; onReread: () => void; rereading: boolean }) {
    const trigger = read.trigger.kind === 'check'
        ? `checks: ${read.trigger.checks.join(', ')}`
        : read.trigger.kind === 'low_confidence'
            ? `confidence ${read.trigger.confidence}`
            : read.trigger.kind;
    const verdict = (v: 'yes' | 'no' | 'unknown') => (v === 'yes' ? 'yes' : v === 'no' ? 'NO' : '?');
    return (
        <div className={`detect-lm-result second-read-panel second-read-panel--${secondReadTone(read)}`}>
            <strong>
                Second read: {read.found ? <code>{read.finalAnswer || '(no answer)'}</code> : 'question not found on the pages'}
                {read.option && <> → option {read.option} ({read.optionSource?.replace('_', ' ')})</>}
            </strong>
            <ul>
                <li>Served: <code>{read.servedAnswer ?? '—'}</code>{read.servedOption && <> → option {read.servedOption}</>}</li>
                <li>Agrees with served: <strong>{verdict(read.agreesWithServed)}</strong> · correct vs key: <strong>{verdict(read.correct)}</strong></li>
                <li>Legibility {read.legibility.toFixed(2)}{read.crossedOut && ' · crossed out'} · trigger: {trigger} · {new Date(read.readAt).toLocaleString()}</li>
                {read.note && <li>Note: {read.note}</li>}
            </ul>
            <button
                type="button"
                className="btn-link second-read-reread"
                onClick={onReread}
                disabled={rereading}
                title="Run the second read again. Replaces the stored read."
            >
                {rereading ? 'Reading...' : 'Read again'}
            </button>
        </div>
    );
}

/** What each reader saw of the answer, and the analyser's reading of each
 *  line — shown beside a doubtful unit so the reviewer compares against them. */
function readerVersionsOf(submission: Submission): import('./EvidencePanel').ReaderVersions {
    const llm = submission.feedback?.llmAssessment as {
        readerConsistency?: { answer?: { answerInExtractedWork?: string; answerInEarlierReading?: string } };
        studentAnswer?: string;
        stepsAnalysis?: { content?: string }[];
    } | undefined;
    const answer = llm?.readerConsistency?.answer;
    return {
        answer: {
            ...(llm?.studentAnswer ? { served: llm.studentAnswer } : {}),
            ...(answer?.answerInExtractedWork ? { extracted: answer.answerInExtractedWork } : {}),
            ...(answer?.answerInEarlierReading ? { earlier: answer.answerInEarlierReading } : {}),
        },
        lines: (llm?.stepsAnalysis ?? []).map(st => st.content ?? ''),
    };
}
